import { CustomFieldType } from '@ee/common/enums';
import { CustomField, LayoutItem } from '@ee/common/models';
import { FieldDefinitionView } from './intake-layout.model';

/**
 * The CustomField `ee-field` draws for one placement: the definition, with this surface's overrides.
 *
 * A placement owns its label, hint, more-info link and requiredness on this surface; everything else —
 * type, options, mask, default, format — is the definition's, except that a placement may show fewer
 * of the definition's choices (`visible_options`). A key with no definition (a field deleted
 * after it was placed) still draws, as a text box named after the key, so a stale layout never blanks
 * the form.
 */
export function customFieldFor(view: FieldDefinitionView | null | undefined, item: LayoutItem): CustomField {
  const base: CustomField = view?.field
    ? { ...view.field }
    : ({
      field: view?.key ?? item.field_key ?? '',
      label: view?.label ?? item.field_key ?? '',
      type: view?.type ?? CustomFieldType.TEXT,
      options: view?.options,
      hint: view?.hint,
      monetary: view?.monetary
    });
  // A form may show fewer of a field's choices than it has; the field's own order is kept.
  const shown = item.visible_options ? new Set(item.visible_options) : null;
  const narrowed: Partial<CustomField> = shown
    ? {
      options: base.options?.filter(option => shown.has(option)),
      ...(base.option_definitions ? { option_definitions: base.option_definitions.filter(option => shown.has(option.value)) } : {})
    }
    : {};
  return {
    ...base,
    ...narrowed,
    // This form's stacked-or-in-a-row, where it has one, over the field's own.
    ...(item.choice_layout ? { choice_layout: item.choice_layout } : {}),
    label: item.label_override || base.label || view?.label || item.field_key || '',
    hint: item.hint || base.hint,
    hint_more_info: item.more_info_link || base.hint_more_info,
    required: item.required_intake ?? base.required ?? false,
    hide_from_intake: false
  };
}
